import { View } from "react-native";
import { IconButton } from "react-native-paper";
import { IconSource } from "react-native-paper/lib/typescript/components/Icon";
import { useTailwind } from "tailwind-rn";

import CardText from "./CardText";
import CreditCards from "../../../logos/CreditCards";
import PaymentMethods from "../../../logos/PaymentMethods";
import {
  CardPaymentMethod,
  PaymentMethod as PaymentMethodType,
} from "../types";

type Props = {
  paymentMethod: PaymentMethodType;
  showText?: boolean;
};
export function PaymentMethod({ paymentMethod, showText }: Props) {
  const tailwind = useTailwind();
  return (
    <View style={tailwind("flex-1 flex-row items-center justify-end")}>
      <PaymentMethodIcon paymentMethod={paymentMethod} />
      {showText && <PaymentMethodText paymentMethod={paymentMethod} />}
    </View>
  );
}

type InternalProps = Pick<Props, "paymentMethod">;

function PaymentMethodText({ paymentMethod }: InternalProps) {
  const tailwind = useTailwind();

  function getText() {
    switch (paymentMethod.type) {
      case "card":
        return `•••• ${paymentMethod.cardLastFour}`;
      case "ach":
        return "ACH";
      case "sinpe":
        return "SINPE";
      case "yappy":
        return "Yappy";
      case "sinpe_movil":
        return "Sinpe Móvil";
      case "cash":
        return "Efectivo";
      case "unspecified":
      case "":
        return "Pago registrado manualmente";
      default:
        return null;
    }
  }

  const text = getText();

  if (!text) {
    return null;
  }

  return <CardText.Label text={text} style={tailwind("ml-1 text-right")} />;
}

function PaymentMethodIcon({ paymentMethod }: InternalProps) {
  const { type } = paymentMethod;

  function renderIcon() {
    switch (type) {
      case "card":
        return <CardIcon paymentMethod={paymentMethod} />;
      case "yappy":
        return <Icon icon={() => <PaymentMethods.Yappy />} />;
      case "sinpe_movil":
        return <Icon icon={() => <PaymentMethods.SinpeMovil />} />;
      default:
        return <PaperIcon type={type} />;
    }
  }

  return renderIcon();
}

function CardIcon({ paymentMethod }: { paymentMethod: CardPaymentMethod }) {
  function renderIcon() {
    switch (paymentMethod.cardType) {
      case "visa":
        return <CreditCards.Visa />;
      case "mastercard":
        return <CreditCards.Mastercard />;
      // fallback to a paper icon
      case "unknown":
      default:
        return <PaperIcon type={paymentMethod.type} />;
    }
  }

  return renderIcon();
}

function PaperIcon({ type }: { type: PaymentMethodType["type"] }) {
  function getIcon() {
    switch (type) {
      case "ach":
      case "sinpe":
        return "bank";
      case "cash":
        return "cash";
      case "card":
        return "credit-card";
      case "yappy":
      case "sinpe_movil":
        return "cellphone";
      case "unspecified":
      case "":
      default:
        return null;
    }
  }

  const icon = getIcon();

  if (!icon) return null;

  return <Icon icon={icon} />;
}

function Icon({ icon }: { icon: IconSource }) {
  const tailwind = useTailwind();
  return <IconButton icon={icon} style={tailwind("m-0 p-0")} />;
}
